
import { toast } from "sonner";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface Template {
  id: string;
  name: string;
  subject: string;
  preview_text?: string;
  content: string;
  created_at: string;
  updated_at: string;
  is_active: boolean;
  created_by?: string;
}

interface TemplateInput {
  id?: string;
  name: string;
  subject: string;
  previewText?: string;
  content: string;
  is_active?: boolean;
}

// Mock templates for fallback
const mockTemplates: Template[] = [
  {
    id: "1",
    name: "Welcome Email",
    subject: "Welcome to our Newsletter!",
    preview_text: "Thank you for subscribing to our newsletter.",
    content: "<h1>Welcome!</h1><p>Thank you for subscribing to our newsletter. We're excited to share our latest updates with you.</p>",
    created_at: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    updated_at: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    is_active: true
  },
  {
    id: "2",
    name: "Monthly Newsletter",
    subject: "Your Monthly Newsletter from {{company_name}}",
    preview_text: "Check out our latest news and updates.",
    content: "<h1>Monthly Newsletter</h1><p>Hello {{subscriber_name}},</p><p>Here are the latest updates from our team.</p>",
    created_at: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
    updated_at: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString(),
    is_active: true
  }
];

export const useNewsletterTemplates = () => {
  const queryClient = useQueryClient();

  // Fetch templates from the database
  const fetchTemplates = async (): Promise<Template[]> => {
    try {
      const { data, error } = await supabase
        .from('newsletter_templates')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      return data.map(template => ({
        id: template.id,
        name: template.name,
        subject: template.subject,
        preview_text: template.preview_text,
        content: template.content,
        created_at: template.created_at,
        updated_at: template.updated_at,
        is_active: template.is_active
      }));
    } catch (error) {
      console.error("Error fetching templates:", error);
      // If in development, return mock data as fallback
      if (process.env.NODE_ENV === 'development') {
        return mockTemplates;
      }
      throw error;
    }
  };

  // Create template function
  const createTemplate = async (template: TemplateInput): Promise<Template> => {
    try {
      const { data, error } = await supabase
        .from('newsletter_templates')
        .insert({
          name: template.name,
          subject: template.subject,
          preview_text: template.previewText,
          content: template.content,
          is_active: template.is_active ?? true
        })
        .select()
        .single();

      if (error) throw error;

      return data;
    } catch (error) {
      console.error("Error creating template:", error);
      // If in development, simulate creation with mock data
      if (process.env.NODE_ENV === 'development') {
        const newTemplate: Template = {
          id: Math.random().toString(36).substr(2, 9),
          name: template.name,
          subject: template.subject,
          preview_text: template.previewText,
          content: template.content,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          is_active: template.is_active ?? true
        };
        mockTemplates.push(newTemplate);
        return newTemplate;
      }
      throw error;
    }
  };

  // Update template function
  const updateTemplate = async (template: TemplateInput): Promise<Template> => {
    try {
      if (!template.id) {
        throw new Error("Template ID is required for updates");
      }

      const { data, error } = await supabase
        .from('newsletter_templates')
        .update({
          name: template.name,
          subject: template.subject,
          preview_text: template.previewText,
          content: template.content,
          is_active: template.is_active
        })
        .eq('id', template.id)
        .select()
        .single();

      if (error) throw error;

      return data;
    } catch (error) {
      console.error("Error updating template:", error);
      // If in development, simulate successful update
      if (process.env.NODE_ENV === 'development') {
        const index = mockTemplates.findIndex(t => t.id === template.id);
        if (index === -1) {
          throw new Error("Template not found");
        }
        
        const updatedTemplate: Template = {
          ...mockTemplates[index],
          name: template.name,
          subject: template.subject,
          preview_text: template.previewText,
          content: template.content,
          updated_at: new Date().toISOString(),
          is_active: template.is_active ?? mockTemplates[index].is_active
        };
        
        mockTemplates[index] = updatedTemplate;
        return updatedTemplate;
      }
      throw error;
    }
  };

  // Delete template function
  const deleteTemplate = async (id: string): Promise<void> => {
    try {
      const { error } = await supabase
        .from('newsletter_templates')
        .delete()
        .eq('id', id);

      if (error) throw error;
    } catch (error) {
      console.error("Error deleting template:", error);
      throw error;
    }
  };

  // Send test email function
  const sendTestEmail = async (templateId: string, email: string): Promise<void> => {
    try {
      // Get the template content
      const { data: template, error: templateError } = await supabase
        .from('newsletter_templates')
        .select('*')
        .eq('id', templateId)
        .single();

      if (templateError) throw templateError;

      // Call our edge function to send the email
      const { error } = await supabase.functions.invoke('send-email', {
        body: {
          to: email,
          subject: template.subject,
          html: template.content,
          preview: template.preview_text,
          type: 'test'
        }
      });

      if (error) throw error;
    } catch (error) {
      console.error("Error sending test email:", error);
      throw error;
    }
  };

  // Set up React Query hooks
  const { data: templates = [], isLoading } = useQuery({
    queryKey: ["newsletterTemplates"],
    queryFn: fetchTemplates,
  });

  // Create template mutation
  const { mutateAsync: createTemplateMutation } = useMutation({
    mutationFn: createTemplate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["newsletterTemplates"] });
      toast.success("Template created successfully");
    },
    onError: (error) => {
      toast.error(`Failed to create template: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  });

  // Update template mutation
  const { mutateAsync: updateTemplateMutation } = useMutation({
    mutationFn: updateTemplate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["newsletterTemplates"] });
      toast.success("Template updated successfully");
    },
    onError: (error) => {
      toast.error(`Failed to update template: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  });

  // Delete template mutation
  const { mutateAsync: deleteTemplateMutation } = useMutation({
    mutationFn: deleteTemplate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["newsletterTemplates"] });
      toast.success("Template deleted successfully");
    },
    onError: (error) => {
      toast.error(`Failed to delete template: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  });

  // Send test email mutation
  const { mutateAsync: sendTestEmailMutation } = useMutation({
    mutationFn: async ({ templateId, email }: { templateId: string; email: string }) => {
      return sendTestEmail(templateId, email);
    },
    onSuccess: () => {
      toast.success("Test email sent successfully");
    },
    onError: (error) => {
      toast.error(`Failed to send test email: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  });

  // Helper function to save (create or update) a template
  const saveTemplate = async (template: TemplateInput) => {
    try {
      if (template.id) {
        await updateTemplateMutation(template);
      } else {
        await createTemplateMutation(template);
      }
    } catch (error) {
      console.error("Error saving template:", error);
      throw error;
    }
  };

  // Helper function to duplicate a template
  const duplicateTemplate = async (template: Template) => {
    try {
      const newTemplate: TemplateInput = {
        name: `${template.name} (Copy)`,
        subject: template.subject,
        previewText: template.preview_text,
        content: template.content,
      };
      await createTemplateMutation(newTemplate);
    } catch (error) {
      console.error("Error duplicating template:", error);
      throw error;
    }
  };

  // Helper function to send a test email
  const sendTestEmailHelper = async (templateId: string, email: string) => {
    try {
      await sendTestEmailMutation({ templateId, email });
    } catch (error) {
      console.error("Error sending test email:", error);
      throw error;
    }
  };

  return {
    templates,
    isLoading,
    saveTemplate,
    deleteTemplate: deleteTemplateMutation,
    duplicateTemplate,
    sendTestEmail: sendTestEmailHelper,
  };
};
